<template>
  <SubHeader></SubHeader>
  <Breadcrumbs :page-name="$route.name"
               :brand-name="details.name"></Breadcrumbs>

  <!-- main -->
  <main class="brand-page flex-grow-1">
    <!-- content -->
    <div class="content py-4">
      <div class="container px-4 px-sm-3">
        <h1 v-if="!details"
            class="mb-3">Brand not found</h1>
        <h1 v-else-if="loading"
            class="mb-3">&#8203;</h1>
        <h1 v-else
            class="mb-3">{{ details.name }}</h1>

        <div v-if="!details"
             class="alert alert-warning">
          <i class="fas fa-exclamation-triangle me-2"></i>We couldn't find any
          brand related to the GBIN number: <strong>{{ props.gbin }}</strong>
        </div>
        <div v-else
             class="row">
          <div class="col-12 col-lg-4">
            <BrandDetails :loading="loading"
                          :details="details"></BrandDetails>
          </div>

          <div class="col-12 col-lg-8 mt-3 mt-lg-0">
            <h3 v-if="loading"
                class="h6">Brand Hierarchy</h3>
            <h3 v-else
                class="h6">{{ hierarchy.name }} - Brand Hierarchy</h3>

            <div class="hierarchy">
              <ul v-if="hierarchy"
                  class="hierarchy__list list-group list-group-flush">
                <BrandHierarchyRow v-if="!loading && hierarchy.gbin != null"
                                   :name="hierarchy.name"
                                   :gbin="hierarchy.gbin"
                                   :logo-url="hierarchy.logoUrl"
                                   :brands="hierarchy.brands"></BrandHierarchyRow>

                <BrandHierarchyRowPlaceholder v-else></BrandHierarchyRowPlaceholder>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- /content -->
  </main>
  <Footer></Footer>
  <!-- /main -->
</template>

<script setup>
import Footer from "@/components/Footer.vue";

import SubHeader from "@/components/SubHeader.vue";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import BrandDetails from "@/components/BrandDetails.vue";
import BrandHierarchyRow from "@/components/BrandHierarchyRow.vue";
import BrandHierarchyRowPlaceholder from "@/components/BrandHierarchyRowPlaceholder.vue";

import { computed, defineProps, onMounted, reactive, ref } from "vue";
import { useHead } from "@vueuse/head";
import { api } from "@/utils/api";

/**
 *  GETTING & LOADING (API) DATA
 */

const props = defineProps({
  name: String,
  gbin: {
    type: String,
    required: true,
  },
});

// App variables
const loading = ref(false);

// API results variables
const details = ref({});
const hierarchy = ref({});

// SEO
const siteData = reactive({
  title: "Brand | GBIN",
  // description: "...",
});

useHead({
  title: computed(() =>
    loading.value ? siteData.title : `${details.value.name} | GBIN`
  ),
  meta: [
    {
      name: `description`,
      content: computed(() => siteData.description),
    },
  ],
});

// MOUNTED
onMounted(async () => {
  getBrandDetails(props.gbin);
});

// Load API Brand Details data
async function getBrandDetails(gbin) {
  loading.value = true;

  try {
    const response = await api.getBrandDetails(gbin);
    details.value = response.data;
    hierarchy.value = response.data.hierarchy;
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
}
</script>

<style lang="scss">
/*  ==========================================================================
    Bootstrap
    ========================================================================== */
@import "~bootstrap/scss/functions";
@import "~bootstrap/scss/variables";
@import "~bootstrap/scss/mixins";

/*  ==========================================================================
    Custom
    ========================================================================== */
@import "@/assets/scss/_variables";

/*  ==========================================================================
    Hierarchy
    ========================================================================== */
/*  Hierarchy > List
    ========================================================================== */
.hierarchy__list-item {
  display: block;

  .hierarchy__list {
    padding: 0 0 0 30px;
  }

  &:first-child {
    .hierarchy__list {
      border-top: 1px solid rgba(0, 0, 0, 0.125);
    }
  }
}

.hierarchy__link {
  padding: 0.5rem 1rem;

  &.router-link-active,
  &:hover {
    background-color: $gbin-primary-faded;
  }
}

.hierarchy__logo {
  flex: 0 0 50px;
}

.hierarchy__name {
  flex: 1;
  color: $black;
}

.hierarchy__gbin {
  color: #9ea4a9;
}
</style>
